import { useURLSearchParams } from "./url"
import { AggregationType } from "../constants/charts"
import useDate from "./date"
import { useMemo } from "react"

const dayjs = useDate()

export const DATE_FORMAT = "YYYY-MM-DD"

const formatDate = (date?: Date) =>
  date ? dayjs(date).startOf("day").format(DATE_FORMAT) : date

const todayDate = new Date()
const today = formatDate(todayDate)
const startOfPrevWeek = dayjs(todayDate)
  .subtract(1, "week")
  .weekday(0)
  .format(DATE_FORMAT)
const endOfPrevWeek = dayjs(todayDate)
  .subtract(1, "week")
  .weekday(6)
  .format(DATE_FORMAT)
const startOfMonth = dayjs(todayDate).startOf("month").format(DATE_FORMAT)
const endOfMonth = dayjs(todayDate).endOf("month").format(DATE_FORMAT)
const sixMonthsAgo = dayjs(todayDate)
  .startOf("day")
  .subtract(6, "month")
  .format(DATE_FORMAT)

export const QUERY_PARAMS = {
  DATE_START: "p_ds",
  DATE_END: "p_de",
  COMPARE_DATE_START: "p_cds",
  COMPARE_DATE_END: "p_cde",
  GRANULARITY: "p_g",
}

const getDateWithFallback = (date?: string) =>
  date && dayjs(date).isValid() ? dayjs(date).format(DATE_FORMAT) : undefined

function getDefaultPeriodForGranularity(
  granularity?: AggregationType,
  maxDate?: Date,
) {
  if (!maxDate) {
    switch (granularity) {
      case AggregationType.WEEKLY:
        return [startOfPrevWeek, endOfPrevWeek]
      case AggregationType.MONTHLY:
        return [startOfMonth, endOfMonth]
      default:
        return [sixMonthsAgo, today]
    }
  }
  const maxDateDJS = dayjs(maxDate)
  switch (granularity) {
    case AggregationType.WEEKLY: {
      const prevWeek = maxDateDJS.subtract(1, "week")
      return [
        prevWeek.startOf("week").format(DATE_FORMAT),
        prevWeek.endOf("week").format(DATE_FORMAT),
      ]
    }
    case AggregationType.MONTHLY: {
      return [
        maxDateDJS.startOf("month").format(DATE_FORMAT),
        maxDateDJS.endOf("month").format(DATE_FORMAT),
      ]
    }
    default:
      return [
        maxDateDJS.subtract(6, "month").format(DATE_FORMAT),
        maxDateDJS.format(DATE_FORMAT),
      ]
  }
}

export function usePeriod(
  defaultGranularity = AggregationType.DAILY,
  resetPeriodOnGranularityChange = false,
  maxDate?: Date,
  defaultDates?: [Date, Date] | null,
) {
  const { params, setParams } = useURLSearchParams()

  const startPeriod = useMemo(
    () => getDateWithFallback(params[QUERY_PARAMS.DATE_START]),
    [params],
  )
  const endPeriod = useMemo(
    () => getDateWithFallback(params[QUERY_PARAMS.DATE_END]),
    [params],
  )
  const compareStartPeriod = useMemo(
    () => getDateWithFallback(params[QUERY_PARAMS.COMPARE_DATE_START]),
    [params],
  )
  const compareEndPeriod = useMemo(
    () => getDateWithFallback(params[QUERY_PARAMS.COMPARE_DATE_END]),
    [params],
  )

  const granularity = useMemo(
    () =>
      params[QUERY_PARAMS.GRANULARITY] in { ...AggregationType }
        ? params[QUERY_PARAMS.GRANULARITY]
        : defaultGranularity,
    [params, defaultGranularity],
  )

  const setPeriod = (dateStart?: string, dateEnd?: string) => {
    setParams({
      [QUERY_PARAMS.DATE_START]: dateStart,
      [QUERY_PARAMS.DATE_END]: dateEnd,
    })
  }

  const setComparePeriod = (dateStart?: string, dateEnd?: string) => {
    setParams({
      [QUERY_PARAMS.COMPARE_DATE_START]: dateStart,
      [QUERY_PARAMS.COMPARE_DATE_END]: dateEnd,
    })
  }

  const setGranularity = (granularity: AggregationType) => {
    setParams({
      [QUERY_PARAMS.GRANULARITY]: granularity,
    })
    if (resetPeriodOnGranularityChange) {
      const [startDate, endDate] = getDefaultPeriodForGranularity(granularity)
      setPeriod(startDate, endDate)
    }
  }

  const defaultPeriodFromGranularity = useMemo(
    () =>
      getDefaultPeriodForGranularity(
        resetPeriodOnGranularityChange
          ? (granularity as AggregationType)
          : undefined,
        maxDate,
      ),
    [resetPeriodOnGranularityChange, granularity, maxDate],
  )

  // Use provided default dates if available, otherwise use granularity-based defaults
  const defaultPeriod = useMemo(() => {
    if (defaultDates) {
      return [
        dayjs(defaultDates[0]).format(DATE_FORMAT),
        dayjs(defaultDates[1]).format(DATE_FORMAT),
      ]
    }
    return defaultPeriodFromGranularity
  }, [defaultDates, defaultPeriodFromGranularity])

  const period = useMemo(
    () => [startPeriod ?? defaultPeriod[0], endPeriod ?? defaultPeriod[1]],
    [startPeriod, endPeriod, defaultPeriod],
  )
  const comparePeriod = useMemo(
    () =>
      compareStartPeriod && compareEndPeriod
        ? [compareStartPeriod, compareEndPeriod]
        : null,
    [compareStartPeriod, compareEndPeriod],
  )

  return {
    period,
    comparePeriod,
    granularity: granularity as AggregationType,
    setPeriod,
    setComparePeriod,
    setGranularity,
  }
}
export const isValidDate = (date: string | number | Date | undefined | null) =>
  dayjs(date).isValid()
